import { locationIndexQueryKey, locationShowOptions, locationShowQueryKey, locationUpdateMutation } from '$api'
import { UpdateLocationForm, UpdateLocationSchema } from '$features/settings/forms/location/update'
import { SettingsRoutePath } from '$features/settings/routes/protected'
import { DateHelper } from '$utils/date'
import { Router } from '$utils/router'
import { NavigableModal, Text } from '$components'
import { LoaderIcon } from '$components'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { ToastHelper } from '$components'
import { ToastType } from '$components'

export function UpdateLocationModal() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { locationId } = useParams()
  const handleClose = () => navigate(Router.buildRoute(SettingsRoutePath.Settings, SettingsRoutePath.SettingsGeneral))

  const { data: location, isLoading: isLocationLoading } = useQuery({
    ...locationShowOptions({
      path: {
        location: Number(locationId),
      },
    }),
  })

  const locationMutation = useMutation({
    ...locationUpdateMutation(),
    onSuccess: async () => {
      ToastHelper.updateSuccess(ToastType.Location)
      queryClient.invalidateQueries({ queryKey: locationIndexQueryKey() })

      await queryClient.invalidateQueries({
        queryKey: locationShowQueryKey({
          path: {
            location: Number(locationId),
          },
        }),
      })

      handleClose()
    },
    onError: () => ToastHelper.updateError(ToastType.Location),
  })

  const handleSubmit = (values: UpdateLocationSchema) => {
    locationMutation.mutate({
      path: { location: Number(locationId) },
      body: {
        name: values.name,
        address: {
          line_one: values.address.line_one,
          line_two: values.address.line_two ?? undefined,
          city: values.address.city,
          post_code: values.address.post_code,
          country: values.address.country,
        },
        business_hours: values.business_hours,
      },
    })
  }

  if (isLocationLoading) {
    return (
      <NavigableModal onClose={handleClose}>
        <NavigableModal.Header>
          <NavigableModal.Heading>
            Update Location
          </NavigableModal.Heading>
        </NavigableModal.Header>

        <NavigableModal.Content>
          {isLocationLoading && (
            <LoaderIcon className="size-5 my-24 mx-auto" />
          )}
        </NavigableModal.Content>
      </NavigableModal>
    )
  }

  if (!location) {
    return (
      <NavigableModal onClose={handleClose}>
        <NavigableModal.Header>
          <NavigableModal.Heading>
            Update Location
          </NavigableModal.Heading>
        </NavigableModal.Header>
        <NavigableModal.Content className="flex items-center justify-center h-full my-24">
          <Text type="s">
            Couldn&apos;t find location.
          </Text>
        </NavigableModal.Content>
      </NavigableModal>
    )
  }

  return (
    <UpdateLocationForm
      isLoading={locationMutation.isPending}
      onSubmit={handleSubmit}
      onCancel={handleClose}
      defaultValues={{
        name: location?.data.name,
        address: {
          country: location?.data.address.country ?? '',
          line_one: location?.data.address.line_one ?? '',
          line_two: location?.data.address.line_two ?? '',
          city: location?.data.address.city ?? '',
          post_code: location?.data.address.post_code ?? '',
        },
        business_hours: location?.data.business_hours.map((businessHour) => ({
          ...businessHour,
          open: DateHelper.toHoursMinutes(businessHour.open),
          close: DateHelper.toHoursMinutes(businessHour.close),
        })),
      }}
    >
      <NavigableModal onClose={handleClose}>
        <NavigableModal.Header>
          <NavigableModal.Heading>
            Update Location
          </NavigableModal.Heading>
        </NavigableModal.Header>

        <NavigableModal.Content>
          <UpdateLocationForm.Content />
        </NavigableModal.Content>

        {location && (
          <NavigableModal.Footer>
            <UpdateLocationForm.Controls />
          </NavigableModal.Footer>
        )}
      </NavigableModal>
    </UpdateLocationForm>
  )
}
